/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT THE CLIENT WILL BE SHOWN — the coach's last feedback, placed on the client's Week (`docs/CARRY-FEEDBACK-PLAN.md`, A).
 * PURE; `carryFeedback.test.ts` runs it.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The phone shows the coach's ONE comment on an exercise the next time the client does it (`CoachSaidLine`, the rule
 * `coachCommentFor` — the Swift's twin, held to it by its corpus). The coach could not see that anywhere. This module
 * answers the coach's question — *where will my comment appear?* — with the SAME rule, never a second one:
 *
 *   for every library exercise with a comment, the comment the rule shows NOW (`before` = now, no current session), placed
 *   on the FIRST row that holds that exercise on a day from TODAY on (the day order, then the workout's order). Later rows
 *   stay clean — the client meets it once, and doing it consumes it.
 *
 * Only what the client can see counts: a workout hidden from the client is not a place the comment can appear.
 */

import { coachCommentFor, sessionFinish, type CommentMark, type CommentSession } from "./coachComment";
import { reviewCarryFor, type CarryReview, type CarrySession } from "./coachReviewCarry";
import { splitTitled } from "./reviewBody";
import { dayMonthLabel } from "./calendar";

/** One exercise row of an upcoming workout: its row id (what the Week draws) and its LIBRARY exercise. */
export type CarryRow = { readonly rowId: string; readonly exerciseId: string | null };
/** One upcoming day: its workouts in the day's order, each one's rows in the workout's order. */
export type CarryDay = { readonly dayKey: string; readonly workouts: readonly (readonly CarryRow[])[] };
/** A comment row as read: the rule's mark, the day of the workout it was about (null when that workout is gone) and
 *  whether it carries a recording or a photo (a comment may be one without words). */
export type CarryComment = CommentMark & { readonly dayKey: string | null; readonly hasMedia: boolean };
/** What the Week draws under the row: `You said · 15/10` and the words. */
export type Said = { readonly commentId: string; readonly dayKey: string | null; readonly text: string };

/** The line's head: `You said · 15/10` (the day of the workout it was about), `You said` when that workout is gone. */
export function saidHead(dayKey: string | null): string {
  return dayKey === null ? "You said" : `You said · ${dayMonthLabel(dayKey)}`;
}

/** The words a comment shows: its text, or — a recording or a photo with no words — a short line saying so. */
export const SAID_MEDIA_ONLY = "A recording or photo — in the chat.";

export function saidText(body: string, hasMedia: boolean): string | null {
  const text = splitTitled(body).text;
  if (text !== "") return text;
  return hasMedia ? SAID_MEDIA_ONLY : null;
}

/** Row id → what the client will be shown on it. */
export function saidByRow(
  days: readonly CarryDay[], comments: readonly CarryComment[], sessions: readonly CommentSession[],
  todayKey: string, now: number,
): Map<string, Said> {
  const out = new Map<string, Said>();
  const upcoming = [...days].filter((d) => d.dayKey >= todayKey).sort((a, b) => (a.dayKey < b.dayKey ? -1 : a.dayKey > b.dayKey ? 1 : 0));
  const exercises = new Set(comments.map((c) => c.coachExerciseId));
  for (const exerciseId of exercises) {
    const shown = coachCommentFor(exerciseId, comments, sessions, null, now);
    if (shown === null) continue;
    const full = comments.find((c) => c.id === shown.id);
    const text = full === undefined ? null : saidText(full.body, full.hasMedia);
    if (full === undefined || text === null) continue;
    const row = firstRow(upcoming, exerciseId);
    if (row !== null) out.set(row, { commentId: full.id, dayKey: full.dayKey, text });
  }
  return out;
}

function firstRow(days: readonly CarryDay[], exerciseId: string): string | null {
  for (const d of days) for (const w of d.workouts) for (const r of w) if (r.exerciseId === exerciseId) return r.rowId;
  return null;
}

/* ── the reads' rows, shaped for the rule (the hook `useCarryFeedback` reads; these are pure so the shaping is tested) ── */

export const CARRY_COMMENT_SELECT = "id, kind, coach_exercise_id, created_at, body, session_id, audio_path, video_path, photo_path";
export const CARRY_SESSION_SELECT =
  "id, completed_at, coach_assignment_id, workout_session_exercises(coach_exercise_id, set_logs(status, completed_at))";
export const CARRY_UPCOMING_SELECT =
  "id, workout_id, root_workout_id, scheduled_date, day_position, hidden_from_client, coach_workouts(coach_workout_sections(position, coach_workout_exercises(id, exercise_id, position, hidden)))";

type One<T> = T | readonly T[] | null | undefined;
const one = <T>(v: One<T>): T | null => (v === null || v === undefined ? null : Array.isArray(v) ? (v[0] ?? null) : (v as T));
const many = <T>(v: One<T>): readonly T[] => (v === null || v === undefined ? [] : Array.isArray(v) ? v : [v as T]);

export type CommentRowIn = {
  id: string; coach_exercise_id: string | null; created_at: string; body: string; session_id: string | null;
  audio_path?: string | null; video_path?: string | null; photo_path?: string | null;
};
export type SessionRowIn = {
  id: string; completed_at: string | null; coach_assignment_id?: string | null;
  workout_session_exercises?: One<{ coach_exercise_id: string | null; set_logs?: One<{ status: string; completed_at: string | null }> }>;
};
export type UpcomingRowIn = {
  id?: string; workout_id?: string; root_workout_id?: string | null;
  scheduled_date: string; day_position: number | null; hidden_from_client: boolean | null;
  coach_workouts?: One<{ coach_workout_sections?: One<{ position: number; coach_workout_exercises?: One<{ id: string; exercise_id: string | null; position: number; hidden?: boolean | null }> }> }>;
};

/** Comments with their library exercise (one without is not on any exercise and is dropped) and their workout's day. */
export function carryComments(rows: readonly CommentRowIn[], sessionDays: ReadonlyMap<string, string>): CarryComment[] {
  return rows.flatMap((r) => r.coach_exercise_id === null ? [] : [{
    id: r.id, coachExerciseId: r.coach_exercise_id, createdAt: r.created_at, body: r.body,
    dayKey: r.session_id === null ? null : (sessionDays.get(r.session_id)?.slice(0, 10) ?? null),
    hasMedia: [r.audio_path, r.video_path, r.photo_path].some((p) => p !== null && p !== undefined && p !== ""),
  }]);
}

export function carrySessions(rows: readonly SessionRowIn[]): CommentSession[] {
  return rows.map((s) => ({
    id: s.id, completedAt: s.completed_at,
    exercises: many(s.workout_session_exercises).map((e) => ({
      coachExerciseId: e.coach_exercise_id,
      sets: many(e.set_logs).map((x) => ({ status: x.status, completedAt: x.completed_at })),
    })),
  }));
}

/** The client's coming days as the client will see them: hidden workouts left out, the day's order, then the workout's. */
export function carryDays(rows: readonly UpcomingRowIn[]): CarryDay[] {
  const byDay = new Map<string, { pos: number; rows: CarryRow[] }[]>();
  for (const a of rows) {
    if (a.hidden_from_client === true) continue;
    const w = one(a.coach_workouts);
    if (w === null) continue;
    const sections = [...many(w.coach_workout_sections)].sort((x, y) => x.position - y.position);
    // F15 4.1b — a row PAUSED by a rule tab is not on the client's day (the door leaves it out), so nothing carries onto it.
    const list = sections.flatMap((s) => [...many(s.coach_workout_exercises)].filter((e) => e.hidden !== true)
      .sort((x, y) => x.position - y.position)
      .map((e) => ({ rowId: e.id, exerciseId: e.exercise_id })));
    const key = a.scheduled_date.slice(0, 10);
    byDay.set(key, [...(byDay.get(key) ?? []), { pos: a.day_position ?? 0, rows: list }]);
  }
  return [...byDay].map(([dayKey, ws]) => ({ dayKey, workouts: [...ws].sort((x, y) => x.pos - y.pos).map((w) => w.rows) }));
}

/* ── B: THE REVIEW OF THE WORKOUT, on its next occurrence (`coachReviewCarry.ts`, the Swift `CoachReviewCarryRule`'s twin) ── */

/** One coming assignment: where it sits and the workout it is (its LINEAGE, `coalesce(root_workout_id, workout_id)`). */
export type CarryAssignment = { readonly assignmentId: string; readonly dayKey: string; readonly dayPosition: number; readonly lineage: string };
/** A review as read: the rule's mark and the day of the workout it was about (null when that workout is gone). */
export type CarryReviewMark = CarryReview & { readonly dayKey: string | null };

/** Assignment id → the review the client will meet there: for each workout among the coming ones, the review the rule
 *  shows NOW, on that workout's FIRST coming occurrence (day, then the day's order). A review without words clears. */
export function saidByAssignment(
  assignments: readonly CarryAssignment[], reviews: readonly CarryReviewMark[], sessions: readonly CarrySession[],
  todayKey: string, now: number,
): Map<string, Said> {
  const out = new Map<string, Said>();
  const coming = assignments.filter((a) => a.dayKey >= todayKey)
    .sort((a, b) => (a.dayKey < b.dayKey ? -1 : a.dayKey > b.dayKey ? 1 : a.dayPosition - b.dayPosition));
  const seen = new Set<string>();
  for (const a of coming) {
    if (seen.has(a.lineage)) continue;
    seen.add(a.lineage);
    const shown = reviewCarryFor(a.lineage, reviews, sessions, null, now);
    const full = shown === null ? undefined : reviews.find((r) => r.id === shown.id);
    const text = full === undefined ? "" : splitTitled(full.body).text;
    if (full !== undefined && text !== "") out.set(a.assignmentId, { commentId: full.id, dayKey: full.dayKey, text });
  }
  return out;
}

/** The coming assignments the client can see, with their lineage (a hidden one is no place). */
export function carryAssignments(rows: readonly UpcomingRowIn[]): CarryAssignment[] {
  return rows.flatMap((a) => (a.hidden_from_client === true || a.id === undefined || a.workout_id === undefined ? [] : [{
    assignmentId: a.id, dayKey: a.scheduled_date.slice(0, 10), dayPosition: a.day_position ?? 0,
    lineage: a.root_workout_id ?? a.workout_id,
  }]));
}

/** Sessions for the review rule: each with the lineage of the assignment it performed (none = invisible to the rule) and
 *  its finish (`sessionFinish`: `completed_at`, else its last logged set). */
export function carryReviewSessions(rows: readonly SessionRowIn[], lineageOf: ReadonlyMap<string, string>): CarrySession[] {
  return carrySessions(rows).map((s, i) => {
    const link = rows[i]?.coach_assignment_id ?? null;
    const f = sessionFinish(s);
    return { id: s.id, lineage: link === null ? null : (lineageOf.get(link) ?? null), finishedAt: f === null ? null : new Date(f).toISOString() };
  });
}
